import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Progress, Stack, Text } from '@eluve/components';
import { type MessageKey, useTranslation } from '@eluve/i18n';

const LOADING_MESSAGE_KEYS = [
  'appointment.insights.loading.reviewingPriorNotes',
  'appointment.insights.loading.summarizingDocumentation',
  'appointment.insights.loading.gatheringInsights',
] as const satisfies readonly MessageKey[];

const EXPECTED_LOAD_TIME_MS = 25_000;
const MESSAGE_DURATION_MS = EXPECTED_LOAD_TIME_MS / LOADING_MESSAGE_KEYS.length;
const COMPLETION_ANIMATION_MS = 400;

// Progress thresholds for asymptotic slowdown
const LINEAR_PROGRESS_CAP = 90; // Progress linearly up to this percentage
const MAX_PROGRESS_CAP = 99; // Never exceed this until actually complete
const SLOWDOWN_DECAY_RATE = 0.0001; // Controls how quickly progress slows after LINEAR_PROGRESS_CAP

/**
 * Calculate progress as percentage of expected time.
 * - Up to EXPECTED_LOAD_TIME_MS: progresses linearly to LINEAR_PROGRESS_CAP (90%)
 * - After EXPECTED_LOAD_TIME_MS: asymptotically approaches MAX_PROGRESS_CAP (99%)
 *   using exponential decay, so it never actually reaches 100% until loading completes
 */
const calculateProgress = (elapsedMs: number): number => {
  if (elapsedMs <= EXPECTED_LOAD_TIME_MS) {
    // Linear progress up to 90% at expected load time
    return (elapsedMs / EXPECTED_LOAD_TIME_MS) * LINEAR_PROGRESS_CAP;
  }

  // After expected time, use asymptotic decay toward MAX_PROGRESS_CAP
  // Formula: 90 + (99 - 90) * (1 - e^(-k * overTime))
  // This approaches 99% but never reaches it
  const overTime = elapsedMs - EXPECTED_LOAD_TIME_MS;
  const remainingProgress = MAX_PROGRESS_CAP - LINEAR_PROGRESS_CAP;
  const decayProgress =
    remainingProgress * (1 - Math.exp(-SLOWDOWN_DECAY_RATE * overTime));

  return LINEAR_PROGRESS_CAP + decayProgress;
};

/**
 * Calculate which message to display based on elapsed time.
 * Messages progress linearly (not cycling), staying on the last message
 * once reached.
 */
const calculateMessageIndex = (elapsedMs: number): number => {
  return Math.min(
    Math.floor(elapsedMs / MESSAGE_DURATION_MS),
    LOADING_MESSAGE_KEYS.length - 1,
  );
};

export interface LoadingInsightsProps {
  /** Timestamp when loading actually started, for accurate progress display */
  loadingStartedAt?: number | null;
  /** When true, quickly animates to 100% before calling onComplete */
  isCompleting?: boolean;
  /** Called when the completing animation finishes */
  onComplete?: () => void;
}

export const LoadingInsights: FC<LoadingInsightsProps> = ({
  loadingStartedAt,
  isCompleting = false,
  onComplete,
}) => {
  const { t } = useTranslation();
  // Use the provided start time, or fall back to mount time if not provided
  const effectiveStartTime = useMemo(
    () => loadingStartedAt ?? Date.now(),
    [loadingStartedAt],
  );

  const getElapsed = useCallback(
    () => Date.now() - effectiveStartTime,
    [effectiveStartTime],
  );

  // Initialize state based on elapsed time (handles case where panel is opened late)
  const [loadingProgress, setLoadingProgress] = useState(() =>
    calculateProgress(getElapsed()),
  );
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(() =>
    calculateMessageIndex(getElapsed()),
  );

  // Track the current progress for capturing at completing start
  const currentProgressRef = useRef(loadingProgress);
  currentProgressRef.current = loadingProgress;

  // Animate loading progress based on elapsed time
  useEffect(() => {
    // Don't run normal progress animation when completing
    if (isCompleting) return;

    const intervalMs = 100;

    const interval = setInterval(() => {
      setLoadingProgress(calculateProgress(getElapsed()));
    }, intervalMs);

    return () => clearInterval(interval);
  }, [getElapsed, isCompleting]);

  // Handle completing animation - quickly fill to 100%
  useEffect(() => {
    if (!isCompleting) return;

    // Capture the current progress when completing starts
    const startProgress = currentProgressRef.current;
    const startTime = Date.now();

    const intervalMs = 16; // ~60fps for smooth animation
    let completionTimeout: ReturnType<typeof setTimeout> | null = null;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / COMPLETION_ANIMATION_MS, 1);
      const newProgress = startProgress + (100 - startProgress) * progress;

      setLoadingProgress(newProgress);

      if (progress >= 1) {
        clearInterval(interval);
        // Small delay to let the 100% progress render before switching views
        completionTimeout = setTimeout(() => {
          onComplete?.();
        }, 250);
      }
    }, intervalMs);

    return () => {
      clearInterval(interval);
      if (completionTimeout) {
        clearTimeout(completionTimeout);
      }
    };
  }, [isCompleting, onComplete]);

  // Progress through messages linearly (not cycling)
  useEffect(() => {
    // Don't run message progression when completing
    if (isCompleting) return;

    const intervalMs = 100;

    const interval = setInterval(() => {
      const newIndex = calculateMessageIndex(getElapsed());
      setLoadingMessageIndex(newIndex);

      // Clear interval once we reach the last message (optimization)
      if (newIndex === LOADING_MESSAGE_KEYS.length - 1) {
        clearInterval(interval);
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [getElapsed, isCompleting]);

  // When completing, show the last message
  const displayedMessageIndex = isCompleting
    ? LOADING_MESSAGE_KEYS.length - 1
    : loadingMessageIndex;

  return (
    <Stack
      className="min-h-[200px]"
      fullWidth={false}
      align="center"
      justify="center"
    >
      <Stack gap={3} align="center" className="max-w-xs">
        <Text color="tertiary" size="xs">
          {t(
            LOADING_MESSAGE_KEYS[displayedMessageIndex] ??
              LOADING_MESSAGE_KEYS[0],
          )}
        </Text>
        <Progress value={loadingProgress} className="h-2 w-full" />
      </Stack>
    </Stack>
  );
};
